import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, api, type Project, type TimeLogCategory } from '@/lib/api';
import { CATEGORIES } from '@/lib/timelogs';

/**
 * まだ行のないところに記録を足す入口。
 *
 * グリッドに行があるプロジェクトはマスを押せば足せるので、ここが要るのは
 * 「その月にまだ 1 件も記録がないプロジェクト」の最初の 1 件だけ。
 *
 * 記録するのは常に**自分の分**。実績の帰属を他人が作れないよう、
 * API も `userId` の自分あて以外を受け付けない。
 */
export function TimeLogCreateForm({
  projects,
  month,
  onCreated,
}: {
  projects: Project[];
  month: string;
  onCreated: () => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [workedOn, setWorkedOn] = useState(`${month}-01`);
  const [hoursValue, setHoursValue] = useState('');
  const [category, setCategory] = useState<TimeLogCategory>('development');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/time-logs', {
        projectId,
        workedOn,
        hours: Number(hoursValue),
        category,
        note,
      });
      setHoursValue('');
      setNote('');
      onCreated();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="記録を追加する"
      className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5"
    >
      <h2 className="text-sm font-bold">記録を追加する</h2>
      <FieldRow>
        <Field label="プロジェクト" className="min-w-52">
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">選択してください</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="作業日" className="w-40">
          <Input
            type="date"
            value={workedOn}
            onChange={(e) => setWorkedOn(e.target.value)}
            className="font-mono"
          />
        </Field>
        <Field label="工数 (h)" className="w-28">
          <Input
            type="number"
            min={0.25}
            max={24}
            step={0.25}
            value={hoursValue}
            onChange={(e) => setHoursValue(e.target.value)}
            className="font-mono"
          />
        </Field>
        <Field label="カテゴリ" className="min-w-36">
          <Select value={category} onChange={(e) => setCategory(e.target.value as TimeLogCategory)}>
            {CATEGORIES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </Select>
        </Field>
      </FieldRow>
      {/* 記録の主経路は MCP で、そこでは改行入りのメモが普通に入る。
          画面から足すときも同じものを書けるようにしておく */}
      <Field label="メモ" className="max-w-3xl">
        <Textarea
          rows={2}
          // 中身がラベル名に混ざらないよう、名前は aria-label で固定する
          aria-label="メモ"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="resize-y"
        />
      </Field>
      <div>
        <Button
          variant="primary"
          onClick={submit}
          disabled={busy || projectId === '' || hoursValue === ''}
        >
          追加
        </Button>
      </div>
      {error ? <p className="text-sm text-alert">{error}</p> : null}
    </section>
  );
}
